import { useEffect, useMemo, useState } from 'react'
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import RowInput from '../components/form/RowInput'
import EmailInputWithDomain from '../components/form/EmailInputWithDomain'
import AddAnotherButton from '../components/form/AddAnotherButton'
import EntryHeader from '../components/form/EntryHeader'
import ShipElsewhereModal from '../components/form/ShipElsewhereModal'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'
import { extractEmailDomain, ordinalLabel, personalizeTitle } from '../lib/formatters'
import { flattenErrorCount, isValidEmail } from '../lib/validators'
import { isValidationDisabled } from '../lib/validationMode'

const STEP_ID = 'kits'

const strictSchema = z.object({
  kits: z
    .array(
      z
        .object({
          quantity: z
            .number({ invalid_type_error: 'Enter 1–99' })
            .int('Enter a whole number')
            .min(1, 'Min 1')
            .max(99, 'Max 99'),
          recipientName: z.string().trim().min(2, 'Required'),
          recipientEmail: z.string().refine(isValidEmail, {
            message: 'Enter a valid email',
          }),
          locationIds: z.array(z.string()).default([]),
          customShipTo: z
            .object({
              addressLine1: z.string().optional().default(''),
              addressLine2: z.string().optional().default(''),
              city: z.string().optional().default(''),
              state: z.string().optional().default(''),
              zip: z.string().optional().default(''),
              country: z.string().optional().default('United States'),
              lat: z.number().nullable().optional(),
              lng: z.number().nullable().optional(),
            })
            .nullable()
            .optional(),
          kitSource: z.enum(['owner', 'provider', 'custom']).optional(),
          isCustomRecipient: z.boolean().optional(),
        })
        .superRefine((kit, ctx) => {
          const hasLoc = Array.isArray(kit.locationIds) && kit.locationIds.length > 0
          const cs = kit.customShipTo
          const hasCustom =
            cs &&
            typeof cs.addressLine1 === 'string' &&
            cs.addressLine1.trim().length > 0 &&
            typeof cs.city === 'string' &&
            cs.city.trim().length > 0
          if (!hasLoc && !hasCustom) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Select at least one location',
              path: ['locationIds'],
            })
          }
        }),
    )
    .optional(),
})
const looseSchema = z.object({
  kits: z.array(z.any()).optional(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

const emptyKit = {
  quantity: 1,
  recipientName: '',
  recipientEmail: '',
  locationIds: [],
  customShipTo: null,
  isCustomRecipient: true,
  kitSource: 'custom',
}

function buildLocationOptions(locations) {
  if (!Array.isArray(locations)) return []
  return locations
    .filter((l) => l?.id)
    .map((l, i) => {
      const label = (l?.addressLine1 || '').trim() || `Location ${i + 1}`
      return { value: l.id, label }
    })
}

function kitFromProvider(p) {
  return {
    quantity: 1,
    recipientName: p?.fullName || '',
    recipientEmail: p?.email || '',
    // Don't pre-seed shipTo — single-select chips render visibly empty so
    // the user makes an explicit choice.
    locationIds: [],
    customShipTo: null,
    isCustomRecipient: false,
    kitSource: 'provider',
  }
}

function kitFromOwner(o) {
  return {
    quantity: 1,
    recipientName: o?.fullName || '',
    recipientEmail: o?.email || '',
    locationIds: [],
    customShipTo: null,
    isCustomRecipient: false,
    kitSource: 'owner',
  }
}

function normEmail(s) {
  return String(s ?? '').trim().toLowerCase()
}

function QuantityCell({ value, onChange }) {
  const [text, setText] = useState(String(value ?? 1))
  useEffect(() => {
    setText(String(value ?? 1))
  }, [value])
  return (
    <RowInput
      type="text"
      inputMode="numeric"
      maxLength={2}
      autoComplete="off"
      placeholder="1"
      value={text}
      onChange={(e) => {
        const d = e.target.value.replace(/\D/g, '').slice(0, 2)
        setText(d)
      }}
      onBlur={() => {
        if (text === '') {
          onChange(1)
          setText('1')
          return
        }
        const n = Number(text)
        const clamped = Math.max(1, Math.min(99, Math.round(n || 1)))
        onChange(clamped)
        setText(String(clamped))
      }}
    />
  )
}

export default function Step7Kits() {
  const step = STEPS.find((s) => s.id === 'kits')
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const locationsData = useOnboardingStore(
    (s) => s.formData.locations?.locations,
  )
  const providersData = useOnboardingStore(
    (s) => s.formData?.providers?.providers,
  )
  const ownersData = useOnboardingStore(
    (s) => s.formData?.owners?.owners,
  )
  const suggested = useOnboardingStore((s) => s.formData?.suggested)
  const practiceWebsite = useOnboardingStore(
    (s) => s.formData?.details?.website,
  )
  const suggestedDomain =
    suggested?.emailDomain || extractEmailDomain(practiceWebsite) || null
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  const [shipModalIdx, setShipModalIdx] = useState(-1)

  const locationOptions = useMemo(
    () => buildLocationOptions(locationsData),
    [locationsData],
  )

  const defaultValues = useMemo(
    () => {
      if (persisted?.kits && persisted.kits.length > 0) {
        return { kits: persisted.kits }
      }
      const primaryOwner = (ownersData || [])[0]
      const ownerSeed =
        primaryOwner && (primaryOwner.email || primaryOwner.fullName)
          ? [kitFromOwner(primaryOwner)]
          : []
      const providerSeeds = (providersData || [])
        .filter((p) => p?.email || p?.fullName)
        .map(kitFromProvider)
      const providerEmails = new Set(
        providerSeeds.map((k) => normEmail(k.recipientEmail)).filter(Boolean),
      )
      // If the primary owner's email matches a provider's, drop the owner kit
      // — the provider row carries location assignments and is the better one.
      const ownerKept = ownerSeed.filter(
        (k) => !providerEmails.has(normEmail(k.recipientEmail)),
      )
      // Zero providers (common under Network+): the demo kit ships to the OWNER,
      // so pre-fill the primary location into shipTo rather than leaving it blank.
      if (providerSeeds.length === 0 && ownerKept[0]) {
        const primaryLocId = (locationsData || []).find((l) => l?.id)?.id
        if (primaryLocId) ownerKept[0].locationIds = [primaryLocId]
      }
      return { kits: [...ownerKept, ...providerSeeds] }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })


  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'kits',
  })
  const watched = useWatch({ control, name: 'kits' })

  // Sync kits with providers: add kit rows for new providers, remove
  // provider-sourced kit rows whose recipient email is no longer in the
  // providers list. Owner-sourced rows and custom rows are left alone.
  useEffect(() => {
    const currentKits = getValues('kits') || []
    const providerEmails = new Set(
      (providersData || []).map((p) => normEmail(p?.email)).filter(Boolean),
    )
    const ownerEmail = normEmail(ownersData?.[0]?.email)

    const retained = currentKits.filter((k) => {
      if (k?.isCustomRecipient) return true
      if (k?.kitSource === 'owner') return true
      const e = normEmail(k?.recipientEmail)
      if (!e) return true
      // Legacy fallback for kits persisted before kitSource existed: a
      // non-custom row matching the primary owner's email is the owner row.
      if (k?.kitSource === undefined && ownerEmail && e === ownerEmail) {
        return true
      }
      return providerEmails.has(e)
    })

    const retainedEmails = new Set(
      retained.map((k) => normEmail(k?.recipientEmail)).filter(Boolean),
    )
    const newKits = (providersData || [])
      .filter((p) => {
        const e = normEmail(p?.email)
        // Don't add a provider row for an email already covered by another
        // kit (owner row or earlier provider row).
        return e && !retainedEmails.has(e)
      })
      .map(kitFromProvider)

    const nextKits = newKits.length > 0 ? [...retained, ...newKits] : retained
    if (nextKits.length !== currentKits.length) {
      replace(nextKits)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providersData])

  const onValid = (data) => {
    updateFormData(STEP_ID, { kits: data.kits ?? [] })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    const token = setHandler(() => submit())
    return () => clearHandler(token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const noLocations = locationOptions.length === 0
  const activeShipModalInitial =
    shipModalIdx >= 0
      ? watched?.[shipModalIdx]?.customShipTo || null
      : null

  return (
    <form
      className={`step-body${liteMode ? ' is-lite-kits' : ''}`}
      onSubmit={handleSubmit(onValid, onInvalid)}
      noValidate
    >
      <StepLayout
        question={personalizeTitle('Where should we ship your kits?')}
        helper={
          liteMode
            ? 'Dose determination card and four non-active sample prescriptions (Bi-est, Progesterone, Testosterone/DHEA for women, Testosterone for men). Free, shipped in a BRITE box.'
            : step.subtitle
        }
      >
        <Band label="Kit shipping" description="Where to send your starter kits">
        <ErrorBanner show={submitAttempted} messages={errorMessages} />
        <EntryHeader noun="kit" count={fields.length} onAdd={() => append({ ...emptyKit, locationIds: [] })} />
        {!liteMode && (
        <details className="inline-disclose">
          <summary>What's in a kit?</summary>
          <div className="inline-disclose-body">
            Dose determination card and four non-active sample prescriptions
            (Bi-est, Progesterone, Testosterone/DHEA for women, Testosterone for
            men). Free, shipped in a BRITE box.
          </div>
        </details>
      )}

      {fields.map((f, idx) => {
        const rowErr = errors?.kits?.[idx]
        const kit = watched?.[idx] || {}
        const isCustom = kit.isCustomRecipient !== false
        const label = ordinalLabel(idx, 'kit')
        const sourceLabel =
          kit.kitSource === 'owner'
            ? 'Practice owner'
            : 'Provider in your team'
        const sourceIcon = kit.kitSource === 'owner' ? '👤' : '🩺'
        const removeButton = (
          <button
            type="button"
            className="team-card-remove"
            aria-label="Remove kit"
            onClick={() => remove(idx)}
          >
            ×
          </button>
        )
        return (
          <Card key={f.id} title={label} actions={removeButton}>
            <FieldRow cols={2}>
              <Field label="Quantity" htmlFor={`kit-${idx}-qty`} error={rowErr?.quantity}>
                <Controller
                  name={`kits.${idx}.quantity`}
                  control={control}
                  render={({ field }) => (
                    <QuantityCell
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Field>

              {!isCustom ? (
                <Field label="Linked to">
                  <div className="kit-source-pill">
                    <span className="kit-source-pill-icon" aria-hidden="true">
                      {sourceIcon}
                    </span>
                    <span className="kit-source-pill-text">{sourceLabel}</span>
                    <button
                      type="button"
                      className="kit-source-pill-unlink"
                      onClick={() =>
                        setValue(`kits.${idx}.isCustomRecipient`, true, {
                          shouldDirty: true,
                        })
                      }
                    >
                      Unlink to edit
                    </button>
                  </div>
                </Field>
              ) : (
                <Field label="Source">
                  <div className="kit-source-pill kit-source-pill--custom">
                    <span className="kit-source-pill-icon" aria-hidden="true">
                      +
                    </span>
                    <span className="kit-source-pill-text">
                      Custom recipient
                    </span>
                  </div>
                </Field>
              )}
            </FieldRow>

            <FieldRow cols={2}>
              <Field label="Recipient name" htmlFor={`kit-${idx}-name`} error={rowErr?.recipientName}>
                <RowInput
                  id={`kit-${idx}-name`}
                  type="text"
                  readOnly={!isCustom}
                  autoComplete="name"
                  placeholder="Who's this kit for?"
                  error={!!rowErr?.recipientName}
                  {...register(`kits.${idx}.recipientName`)}
                />
              </Field>

              <Field label="Recipient email" htmlFor={`kit-${idx}-email`} error={rowErr?.recipientEmail}>
                <Controller
                  name={`kits.${idx}.recipientEmail`}
                  control={control}
                  render={({ field }) => (
                    <EmailInputWithDomain
                      id={`kit-${idx}-email`}
                      value={field.value}
                      onChange={field.onChange}
                      suggestedDomain={isCustom ? suggestedDomain : null}
                      readOnly={!isCustom}
                      autoComplete="email"
                      placeholder={
                        isCustom && suggestedDomain
                          ? `you@${suggestedDomain}`
                          : 'Work email'
                      }
                      error={!!rowErr?.recipientEmail}
                    />
                  )}
                />
              </Field>
            </FieldRow>

            <Field
              label="Ship to"
              error={rowErr?.locationIds}
              help={liteMode ? 'Pick where to ship this kit, or add a new address.' : undefined}
            >
              {locationOptions.length === 0 ? (
                <div className="team-card-empty-hint">
                  Add at least one location in Step 5 to ship kits.
                </div>
              ) : (
                <Controller
                  name={`kits.${idx}.locationIds`}
                  control={control}
                  render={({ field }) => {
                    // No ship-to chosen yet → expose a wire target so the
                    // FieldGuideWire points users at this required SELECTION
                    // (the prefilled name/email make it look already-done).
                    const hasShipTo =
                      !!kit.customShipTo ||
                      (Array.isArray(field.value) && field.value.length > 0)
                    return (
                    <>
                    <div className="team-card-chips" data-fgw-target={hasShipTo ? undefined : 'true'}>
                      {locationOptions.map((opt) => {
                        const selected =
                          !kit.customShipTo &&
                          Array.isArray(field.value) &&
                          field.value.includes(opt.value)
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className="team-card-chip"
                            aria-pressed={selected}
                            onClick={() => {
                              field.onChange([opt.value])
                              setValue(
                                `kits.${idx}.customShipTo`,
                                null,
                                { shouldDirty: true },
                              )
                            }}
                          >
                            {opt.label}
                          </button>
                        )
                      })}
                      <button
                        type="button"
                        className={`team-card-chip team-card-chip--ghost${
                          kit.customShipTo ? ' is-active' : ''
                        }`}
                        aria-pressed={!!kit.customShipTo}
                        onClick={() => setShipModalIdx(idx)}
                      >
                        + Ship somewhere else
                      </button>
                      {kit.customShipTo && (
                        <div className="kit-custom-shipto-summary">
                          <span>
                            Custom:{' '}
                            {[
                              kit.customShipTo.addressLine1,
                              kit.customShipTo.city,
                            ]
                              .filter(Boolean)
                              .join(', ') || '(address pending)'}
                          </span>
                          <button
                            type="button"
                            className="kit-custom-shipto-clear"
                            onClick={() =>
                              setValue(`kits.${idx}.customShipTo`, null, {
                                shouldDirty: true,
                              })
                            }
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                    {!hasShipTo && (
                      <p className="kit-shipto-hint">Select where this kit ships</p>
                    )}
                    </>
                    )
                  }}
                />
              )}
            </Field>
          </Card>
        )
      })}

      <AddAnotherButton
        label={fields.length === 0 ? 'Add a kit' : 'Add another kit'}
        onClick={() => append({ ...emptyKit, locationIds: [] })}
      />
      {fields.length === 0 && (
        <button
          type="button"
          className="team-skip-link"
          onClick={() => nextStep()}
        >
          Or skip — you can request kits later from your dashboard.
        </button>
      )}

      {noLocations && fields.length > 0 && (
        <div className="field-error" style={{ marginTop: 8 }}>
          Add locations in Step 5 first to assign kits.
        </div>
      )}
      </Band>
      </StepLayout>

      {shipModalIdx >= 0 && (
        <ShipElsewhereModal
          initial={activeShipModalInitial}
          onClose={() => setShipModalIdx(-1)}
          onSave={(addr) => {
            setValue(`kits.${shipModalIdx}.customShipTo`, addr, {
              shouldDirty: true,
              shouldValidate: true,
            })
            setValue(`kits.${shipModalIdx}.locationIds`, [], {
              shouldDirty: true,
            })
            setShipModalIdx(-1)
          }}
        />
      )}

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
